import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from '@src/service/axios';
import { toast } from 'react-toastify';
import { getDeviceInfo } from '../../../../utility/Utils';
import queryString from 'query-string';

// Async thunks
export const getProfileData = createAsyncThunk('profileSlice/getProfileData', async () => {
    try {
        const response = await axios.get('/profile', {
            headers: { 'Content-Type': 'application/json' }
        });
        return response.data.data;
    } catch (err) {
        toast.error(err.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const updateProfile = createAsyncThunk('profileSlice/updateProfile', async (data, { dispatch }) => {
    try {
        const response = await axios.put('/profile', data, {
            headers: { 'Content-Type': 'multipart/form-data' }
            // loader: true
        });
        toast.success(response?.data?.message || 'Profile updated successfully!');
        await dispatch(getProfileData());
        return response.data;
    } catch (err) {
        toast.error(err.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const updateImage = createAsyncThunk('profileSlice/updateImage', async ({ id, data }, { dispatch }) => {
    try {
        const response = await axios.put('/user/update-image/' + `${id}`, data, {
            headers: { 'Content-Type': 'multipart/form-data' },
            loader: true
        });
        toast.success(response?.data?.message || 'Image updated successfully!');
        await dispatch(getProfileData());
        return Promise.resolve(response.data);
    } catch (err) {
        toast.error(err.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const deleteImage = createAsyncThunk('profileSlice/deleteImage', async (data, { dispatch }) => {
    try {
        const response = await axios.delete('/user/remove-profile-cover', {
            params: data,
            headers: { 'Content-Type': 'application/json' },
            loader: true
        });
        toast.success(response?.data?.message || 'Profile image deleted successfully!');

        await dispatch(getProfileData());
        return response.data;
    } catch (err) {
        toast.error(err.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const deleteImageById = createAsyncThunk('profileSlice/deleteImageById', async ({ id, data }, { dispatch }) => {
    try {
        const response = await axios.delete(`/user/remove-image/${id}`, {
            params: data,
            headers: { 'Content-Type': 'application/json' },
            loader: true
        });
        toast.success(response?.data?.message || 'Remove image successfully!');
        await dispatch(getProfileData());
        return Promise.resolve(response.data);
    } catch (err) {
        toast.error(err.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const getActivity = createAsyncThunk('profileSlice/getActivity', async () => {
    try {
        const response = await axios.get('/user-activity', {
            headers: { 'Content-Type': 'application/json' }
        });
        return response.data.data;
    } catch (err) {
        toast.error(err.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const sendCode = createAsyncThunk('profileSlice/sendCode', async (data) => {
    try {
        const response = await axios.post('/user/send-code', data, {
            headers: { 'Content-Type': 'application/json' },
            loader: true
        });
        // toast.success(response?.data?.message || 'Mail sent successfully');
        return response.data;
    } catch (err) {
        toast.error(err.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const verifyEmailChange = createAsyncThunk('profileSlice/verifyEmailChange', async () => {
    try {
        const response = await axios.get('/user/check-email', {
            headers: { 'Content-Type': 'application/json' }
        });
        return response.data.data.email;
    } catch (err) {
        toast.error(err.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const verifyCode = createAsyncThunk('profileSlice/verifyCode', async (data) => {
    try {
        const response = await axios.post('/user/verify-code', data, {
            headers: { 'Content-Type': 'application/json' },
            loader: true
        });
        return response.data;
    } catch (err) {
        toast.error(err.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const clearEmail = createAsyncThunk('profileSlice/clearEmail', async (_, { dispatch }) => {
    try {
        const response = await axios.delete('/user/clear-email', {
            headers: { 'Content-Type': 'application/json' },
            loader: true
        });
        toast.success(response?.data?.message || 'Email deleted successfully!');

        await dispatch(verifyEmailChange());
        return response.data;
    } catch (err) {
        toast.error(err.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const getRecentDevices = createAsyncThunk('profileSlice/getRecentDevices', async (id) => {
    try {
        const query = queryString.stringify(id ? id : null);
        const response = await axios.get('/user-session' + `?${query}`, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data.data;
    } catch (err) {
        toast.error(err.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const deleteSession = createAsyncThunk('profileSlice/deleteSession', async (deleteId, { dispatch }) => {
    try {
        const response = await axios.delete('/user-session/' + deleteId, {
            headers: { 'Content-Type': 'application/json' },
            loader: true
        });
        toast.success(response?.data?.message || 'Session deleted successfully!');

        return response.data;
    } catch (err) {
        toast.error(err.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const deleteSessionInBulk = createAsyncThunk('profileSlice/deleteSessionInBulk', async (data) => {
    try {
        const response = await axios.delete('/user-session/bulk-delete', {
            data,
            headers: { 'Content-Type': 'application/json' },
            loader: true
        });
        toast.success(response?.data?.message || 'Sessions deleted successfully!');

        return response.data;
    } catch (err) {
        toast.error(err.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

const profileSlice = createSlice({
    name: 'profileSlice',
    initialState: {
        profile: {},
        email: '',
        recentDevices: []
    },
    reducers: {
        removeProfileData(state) {
            state.profile = {};
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getProfileData.fulfilled, (state, action) => {
            state.profile = action.payload;
            return;
        });
        builder.addCase(verifyEmailChange.fulfilled, (state, action) => {
            state.email = action.payload;
            return;
        });
        builder.addCase(getRecentDevices.fulfilled, (state, action) => {
            state.recentDevices = action.payload;
            return;
        });
    }
});

export const { removeProfileData } = profileSlice.actions;
export default profileSlice.reducer;
